import { useState, useRef, useCallback, useLayoutEffect, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';

type Side = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  /** Tooltip content (typically the full text of a truncated label). */
  label: ReactNode;
  /** The trigger element the tooltip describes. */
  children: ReactNode;
  /** Preferred side (kept for API compatibility; the bubble follows the cursor). */
  side?: Side;
  /** Extra classes for the inline-flex wrapper around the trigger. */
  className?: string;
  /** Skip the tooltip entirely (e.g. when the label isn't actually truncated). */
  disabled?: boolean;
  /** Max bubble width in px (long text wraps). */
  maxWidth?: number;
}

const GAP = 14; // distance from the cursor
const MARGIN = 8; // min distance from any viewport edge

/**
 * App-wide themed tooltip. Replaces the browser's default `title` hover (which can't be
 * styled and looks out of place). The bubble follows the mouse pointer, is rendered in a
 * portal (so `overflow-hidden` parents / tables never clip it), and is measured then
 * clamped to stay fully on-screen — so it's always visible, including for elements at the
 * very top/edges of the page. Colours come from the project theme CSS variables, so it
 * matches light & dark in both the local and cloud apps.
 */
export default function Tooltip({
  label,
  children,
  className = '',
  disabled = false,
  maxWidth = 320,
}: TooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);

  // Follow the cursor so the tooltip sits right beside the pointer (updates as it moves).
  const track = useCallback((e: ReactMouseEvent) => {
    if (disabled || label == null || label === '') return;
    setAnchor({ x: e.clientX, y: e.clientY });
  }, [disabled, label]);

  // Keyboard focus has no cursor → anchor to the trigger's bottom edge.
  const onFocus = useCallback(() => {
    if (disabled || label == null || label === '') return;
    const r = ref.current?.getBoundingClientRect();
    if (r) setAnchor({ x: r.left + r.width / 2, y: r.bottom });
  }, [disabled, label]);

  const hide = useCallback(() => { setAnchor(null); setBox(null); }, []);

  // Measure the rendered bubble, then place it beside the cursor and clamp to the viewport
  // (flip to the other side of the cursor if it would overflow; never past the edges).
  useLayoutEffect(() => {
    if (!anchor || !bubbleRef.current) return;
    const b = bubbleRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchor.x + GAP;
    if (left + b.width > vw - MARGIN) left = anchor.x - GAP - b.width; // flip to the left of cursor
    left = Math.max(MARGIN, Math.min(left, vw - b.width - MARGIN));

    let top = anchor.y + GAP;
    if (top + b.height > vh - MARGIN) top = anchor.y - GAP - b.height;  // flip above cursor
    top = Math.max(MARGIN, Math.min(top, vh - b.height - MARGIN));

    setBox({ left, top });
  }, [anchor]);

  return (
    <span
      ref={ref}
      className={`inline-flex ${className}`}
      onMouseEnter={track}
      onMouseMove={track}
      onMouseLeave={hide}
      onFocus={onFocus}
      onBlur={hide}
    >
      {children}
      {anchor && createPortal(
        <div
          ref={bubbleRef}
          role="tooltip"
          className="pointer-events-none fixed z-[9999] rounded-md px-2 py-1 text-xs font-medium leading-snug break-words"
          style={{
            // Off-screen until measured (avoids a one-frame flash at the wrong spot).
            left: box ? box.left : -9999,
            top: box ? box.top : -9999,
            opacity: box ? 1 : 0,
            maxWidth,
            background: 'var(--popover)',
            color: 'var(--popover-foreground)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
          }}
        >
          {label}
        </div>,
        document.body,
      )}
    </span>
  );
}
